import { useCallback, useEffect, useState } from 'react'

/**
 * O convite de instalacao do navegador, guardado pra disparar do nosso botao.
 *
 * O Chrome/Edge avisa que a pagina e instalavel pelo evento
 * `beforeinstallprompt`. Ele so continua valendo se alguem chamar
 * `preventDefault()` nele: sem isso o navegador mostra a barrinha dele e
 * descarta o evento — e ai nao ha o que um botao na pagina possa disparar.
 *
 * Firefox e Safari nao tem esse evento. La a instalacao existe, mas so pelo
 * menu do proprio navegador ("Adicionar a Tela de Inicio"), e nenhuma pagina
 * consegue abrir esse menu. Por isso o botao some em vez de aparecer sem
 * funcionar: um botao que nao faz nada e pior que a ausencia dele.
 */
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** Ja esta rodando na janela propria (instalado), e nao numa aba? */
function rodandoInstalado(): boolean {
    if (typeof window === 'undefined') return false
    // `standalone` e o caminho do iOS, onde nao ha display-mode.
    if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) return true
    return window.matchMedia?.('(display-mode: standalone)')?.matches === true
}

export function useInstallPrompt() {
    const [convite, setConvite] = useState<BeforeInstallPromptEvent | null>(null)
    const [instalado, setInstalado] = useState(rodandoInstalado)

    useEffect(() => {
        const guardar = (event: Event) => {
            event.preventDefault()
            setConvite(event as BeforeInstallPromptEvent)
        }
        // Instalou por qualquer caminho (nosso botao ou o icone da barra de
        // endereco): o convite morre junto, e o botao sai da tela.
        const instalou = () => {
            setInstalado(true)
            setConvite(null)
        }

        window.addEventListener('beforeinstallprompt', guardar)
        window.addEventListener('appinstalled', instalou)
        return () => {
            window.removeEventListener('beforeinstallprompt', guardar)
            window.removeEventListener('appinstalled', instalou)
        }
    }, [])

    const [abrindo, setAbrindo] = useState(false)

    const instalar = useCallback(async () => {
        // A trava vale contra o segundo clique: `prompt()` no mesmo evento duas
        // vezes da erro, e o dialogo do navegador demora o quanto a pessoa
        // demorar pra responder.
        if (!convite || abrindo) return
        setAbrindo(true)
        try {
            await convite.prompt()
            const { outcome } = await convite.userChoice
            if (outcome === 'accepted') setInstalado(true)
        } catch {
            // Dialogo recusado pelo navegador: segue no navegador mesmo.
        }
        // Aceito ou nao, o convite se gasta. Quem recusou ganha outro numa
        // proxima visita — quem tentar de novo agora so veria um botao morto.
        setConvite(null)
        setAbrindo(false)
    }, [convite, abrindo])

    return {
        /** Da pra oferecer a instalacao agora? */
        podeInstalar: !!convite && !instalado,
        /** O dialogo do navegador esta aberto, esperando resposta. */
        abrindo,
        instalado,
        instalar
    }
}
